import { Suspense, type ReactElement } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { GameProvider } from './context/GameContext';
import { DebugSegmentsEntry } from './components/DebugSegmentsPanel';
import { ErrorBoundary, GameErrorBoundary } from './components/ErrorBoundary';
import FloatingControls from './components/FloatingControls';
import MobilePullToRefresh from './components/MobilePullToRefresh';
import { BackNavigationProvider } from './context/BackNavigationContext';
import { useI18n } from './i18n/I18nProvider';
import NativeAppUrlListener from './components/NativeAppUrlListener';
import PushNotificationsBridge from './components/PushNotificationsBridge';
import AuthCallbackInterceptor from './components/AuthCallbackInterceptor';
import FloatingControlsError from './components/fallbacks/FloatingControlsError';
import { isDevEnv } from './utils/debugFlags';
import { lazyWithReload } from './utils/lazyWithReload';

import Home from './pages/Home';
const Game = lazyWithReload(() => import('./pages/Game'), 'Game');
const HostGame = lazyWithReload(() => import('./pages/HostGame'), 'HostGame');
const JoinGame = lazyWithReload(() => import('./pages/JoinGame'), 'JoinGame');
const OnlineGameOverRedirect = lazyWithReload(() => import('./pages/OnlineGameOverRedirect'), 'OnlineGameOverRedirect');
const OnlineRoom = lazyWithReload(() => import('./pages/OnlineRoom'), 'OnlineRoom');
const RealLobby = lazyWithReload(() => import('./pages/RealLobby'), 'RealLobby');

const GameOver = lazyWithReload(() => import('./pages/GameOver'), 'GameOver');
const GameResults = lazyWithReload(() => import('./pages/GameResults'), 'GameResults');
const Scores = lazyWithReload(() => import('./pages/Scores'), 'Scores');
const Guide = lazyWithReload(() => import('./pages/Guide'), 'Guide');
const Stats = lazyWithReload(() => import('./pages/Stats'), 'Stats');
const Account = lazyWithReload(() => import('./pages/Account'), 'Account');
const AccountStats = lazyWithReload(() => import('./pages/AccountStats'), 'AccountStats');
const AccountSocial = lazyWithReload(() => import('./pages/AccountSocial'), 'AccountSocial');
const AccountDelete = lazyWithReload(() => import('./pages/AccountDelete'), 'AccountDelete');
const AuthCallback = lazyWithReload(() => import('./pages/AuthCallback'), 'AuthCallback');
const PrivacyPolicy = lazyWithReload(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const TermsOfService = lazyWithReload(() => import('./pages/TermsOfService'), 'TermsOfService');
const ScenarioPicker = lazyWithReload(() => import('./pages/ScenarioPicker'), 'ScenarioPicker');

type GameMode = 'REAL' | 'ONLINE';

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center px-6 py-10 text-center text-sm text-brand-text/70">
      Loading...
    </div>
  );
}

function withRouteSuspense(element: ReactElement) {
  return (
    <Suspense fallback={<RouteFallback />}>
      {element}
    </Suspense>
  );
}

function ModeShell({ mode }: { mode: GameMode }) {
  const { t } = useI18n();

  return (
    <GameProvider modeHint={mode}>
      <BackNavigationProvider>
        <div className="min-h-screen bg-brand-dark text-brand-text">
          <a href="#main" className="skip-link">{t('app.skip')}</a>
          <main id="main">
            <Outlet />
          </main>
          <MobilePullToRefresh />
          <ErrorBoundary fallback={FloatingControlsError}>
            <FloatingControls />
          </ErrorBoundary>
          <DebugSegmentsEntry />
        </div>
      </BackNavigationProvider>
    </GameProvider>
  );
}

function App() {
  const showDevScoresRoute = isDevEnv() || import.meta.env.MODE === 'test';
  const showDevScenarios = isDevEnv() || import.meta.env.MODE === 'test';

  return (
    <GameErrorBoundary>
      <NativeAppUrlListener />
      <PushNotificationsBridge />
      <AuthCallbackInterceptor />
      <Routes>
        <Route path="/" element={<ModeShell mode="REAL" />}>
          <Route index element={<Home />} />
          {showDevScoresRoute ? <Route path="scores" element={withRouteSuspense(<Scores />)} /> : null}
          {showDevScenarios ? <Route path="scenarios" element={withRouteSuspense(<ScenarioPicker />)} /> : null}
          <Route path="results/:gameId" element={withRouteSuspense(<GameResults />)} />
          <Route path="guide" element={withRouteSuspense(<Guide />)} />
          <Route path="stats" element={withRouteSuspense(<Stats />)} />
          <Route path="account" element={withRouteSuspense(<Account />)} />
          <Route path="account/results" element={withRouteSuspense(<Scores />)} />
          <Route path="account/results/:gameId" element={withRouteSuspense(<GameResults />)} />
          <Route path="account/stats" element={withRouteSuspense(<AccountStats />)} />
          <Route path="account/social" element={withRouteSuspense(<AccountSocial />)} />
          <Route path="account/delete" element={withRouteSuspense(<AccountDelete />)} />
          <Route path="auth/callback" element={withRouteSuspense(<AuthCallback />)} />
          <Route path="privacy" element={withRouteSuspense(<PrivacyPolicy />)} />
          <Route path="terms" element={withRouteSuspense(<TermsOfService />)} />
        </Route>

        <Route path="/real" element={<ModeShell mode="REAL" />}>
          <Route index element={withRouteSuspense(<RealLobby />)} />
          <Route path="game" element={withRouteSuspense(<Game />)} />
          <Route path="game/:gameId" element={withRouteSuspense(<Game />)} />
          <Route path="host/:gameId" element={withRouteSuspense(<HostGame />)} />
          <Route path="join" element={withRouteSuspense(<JoinGame initialMode="real" forceMode />)} />
          <Route path="join/:gameId" element={withRouteSuspense(<JoinGame initialMode="real" forceMode />)} />
          <Route path="game-over" element={withRouteSuspense(<GameOver />)} />
          <Route path="results/:gameId" element={withRouteSuspense(<GameResults />)} />
          {showDevScoresRoute ? <Route path="scores" element={withRouteSuspense(<Scores />)} /> : null}
          <Route path="guide" element={withRouteSuspense(<Guide />)} />
          <Route path="stats" element={withRouteSuspense(<Stats />)} />
          <Route path="account" element={withRouteSuspense(<Account />)} />
          <Route path="account/results" element={withRouteSuspense(<Scores />)} />
          <Route path="account/results/:gameId" element={withRouteSuspense(<GameResults />)} />
          <Route path="account/stats" element={withRouteSuspense(<AccountStats />)} />
          <Route path="account/social" element={withRouteSuspense(<AccountSocial />)} />
          <Route path="account/delete" element={withRouteSuspense(<AccountDelete />)} />
        </Route>

        <Route path="/online" element={<ModeShell mode="ONLINE" />}>
          <Route index element={withRouteSuspense(<OnlineRoom />)} />
          <Route path="game/:gameId" element={withRouteSuspense(<Game />)} />
          <Route path="game-over" element={withRouteSuspense(<OnlineGameOverRedirect />)} />
          <Route path="results/:gameId" element={withRouteSuspense(<GameResults />)} />
          {showDevScoresRoute ? <Route path="scores" element={withRouteSuspense(<Scores />)} /> : null}
          <Route path="guide" element={withRouteSuspense(<Guide />)} />
          <Route path="stats" element={withRouteSuspense(<Stats />)} />
          <Route path="account" element={withRouteSuspense(<Account />)} />
          <Route path="account/results" element={withRouteSuspense(<Scores />)} />
          <Route path="account/results/:gameId" element={withRouteSuspense(<GameResults />)} />
          <Route path="account/stats" element={withRouteSuspense(<AccountStats />)} />
          <Route path="account/social" element={withRouteSuspense(<AccountSocial />)} />
          <Route path="account/delete" element={withRouteSuspense(<AccountDelete />)} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </GameErrorBoundary>
  );
}

export default App;
